import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  RateReview as ReviewIcon,
  TaskAlt as CompleteIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

const statusChip = {
  pending: { label: "Awaiting review", color: "#ed6c02", bg: "#ed6c0218" },
  approved: { label: "Approved", color: "#2e7d32", bg: "#2e7d3218" },
  rejected: { label: "Rejected", color: "#d32f2f", bg: "#d32f2f18" },
};

/** The two-stage chain, keyed on the server's `stage` field. */
const stageChip = {
  awaiting_tutor: { label: "Awaiting lead tutor", color: "#ed6c02", bg: "#ed6c0218" },
  rejected_by_tutor: { label: "Rejected by lead tutor", color: "#d32f2f", bg: "#d32f2f18" },
  awaiting_admin: { label: "Awaiting you", color: "#0288d1", bg: "#0288d118" },
  approved: { label: "Approved", color: "#2e7d32", bg: "#2e7d3218" },
  completed: { label: "Complete", color: "#2e7d32", bg: "#2e7d3218" },
  rejected: { label: "Rejected", color: "#d32f2f", bg: "#d32f2f18" },
};

const chipFor = (r) => stageChip[r.stage] || statusChip[r.approval_status] || statusChip.pending;

/** Filter values the server reads as `stage` rather than `status`. */
const STAGE_FILTERS = ["awaiting_admin", "awaiting_tutor", "completed"];

/**
 * "Lead tutor ✓ → Admin" — where this item is in the chain.
 *
 * Reading `stage` rather than the two status columns keeps the indicator in step with the server's
 * own definition of the chain, which is where the guards live.
 */
function StageSteps({ row }) {
  const leadDone = row.lead_status === "approved";
  const leadRejected = row.lead_status === "rejected";
  const adminDone = row.approval_status === "approved";
  const adminRejected = row.approval_status === "rejected" && !leadRejected;

  const step = (label, done, rejected) => (
    <Chip
      size="small"
      variant={done || rejected ? "filled" : "outlined"}
      label={`${label}${done ? " ✓" : rejected ? " ✕" : ""}`}
      sx={{
        fontWeight: 600,
        ...(done && { bgcolor: "#2e7d3218", color: "#2e7d32" }),
        ...(rejected && { bgcolor: "#d32f2f18", color: "#d32f2f" }),
      }}
    />
  );

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {step("Lead tutor", leadDone, leadRejected)}
      <Typography variant="caption" color="text.secondary">
        →
      </Typography>
      {step("Admin", adminDone, adminRejected)}
      {row.completed_at && step("Complete", true, false)}
    </Stack>
  );
}

/**
 * Admin review queue for tutor-uploaded content.
 *
 * Nothing a tutor uploads reaches students until it is approved here — the gate is
 * ContentVerification, which is default-deny for tutor resources.
 */
export default function TutorResourceReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Default to the admin's own queue — items the lead tutor has already cleared.
  const [filter, setFilter] = useState("awaiting_admin");

  const [target, setTarget] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Stage filters go on `stage`; the flat approved/rejected ones stay on `status`.
      const params = STAGE_FILTERS.includes(filter) ? { stage: filter } : { status: filter };
      const res = await api.get("admin/tutor-resources", { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load tutor resources.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  /** The last step: bookkeeping only — visibility already changed on approval. */
  const complete = async (row) => {
    setCompleting(row.id);
    try {
      await api.post(`admin/tutor-resources/${row.id}/complete`);
      toast.success("Marked complete.");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.data?.error || e?.response?.data?.message || "Could not mark it complete."
      );
    } finally {
      setCompleting(null);
    }
  };

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`admin/tutor-resources/${target.row.id}/review`, {
        status: target.status,
        notes: notes || null,
      });
      toast.success(
        target.status === "approved"
          ? "Approved — now visible to the group."
          : "Rejected — it stays hidden from students."
      );
      setTarget(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save the review.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReviewIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Tutor Content Review
          </Typography>
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Content uploaded by tutors, after the group's lead tutor has cleared it. Nothing here is
        visible to students until you approve it.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="awaiting_admin">Awaiting you</ToggleButton>
        <ToggleButton value="awaiting_tutor">With lead tutor</ToggleButton>
        <ToggleButton value="approved">Approved</ToggleButton>
        <ToggleButton value="completed">Complete</ToggleButton>
        <ToggleButton value="rejected">Rejected</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={120} />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {filter === "awaiting_admin"
            ? "Nothing is awaiting your review."
            : "No content in this state."}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {rows.map((r) => {
            const chip = chipFor(r);
            // The server refuses an admin verdict until the lead tutor has cleared it, so the
            // buttons must not offer one either.
            const leadCleared = r.lead_status === undefined || r.lead_status === "approved";
            return (
              <Card key={r.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${chip.color}` }}>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ md: "center" }}
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }}
                        />
                        {r.group?.name && <Chip size="small" variant="outlined" label={r.group.name} />}
                        {r.subject?.name && <Chip size="small" variant="outlined" label={r.subject.name} />}
                        {r.topic?.name && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name}
                          />
                        )}
                      </Stack>
                      <Typography variant="h6" fontWeight={700}>
                        {r.title}
                      </Typography>
                      {r.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {r.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Uploaded by {r.uploaded_by || "a tutor"}
                        {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ""}
                        {r.lead_reviewed_by ? ` · cleared by ${r.lead_reviewed_by}` : ""}
                      </Typography>
                      {r.lead_notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lead tutor's note: {r.lead_notes}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <StageSteps row={r} />
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                      {r.file_url && (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          component="a"
                          href={r.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </Button>
                      )}
                      {r.approval_status === "approved" && !r.completed_at && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CompleteIcon />}
                          disabled={completing === r.id}
                          onClick={() => complete(r)}
                        >
                          {completing === r.id ? "Saving…" : "Complete"}
                        </Button>
                      )}
                      {r.approval_status !== "approved" && leadCleared && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => setTarget({ row: r, status: "approved" })}
                        >
                          Approve
                        </Button>
                      )}
                      {r.approval_status !== "approved" && !leadCleared && (
                        <Tooltip title="The group's lead tutor has not approved this yet">
                          <span>
                            <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} disabled>
                              Approve
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {r.approval_status !== "rejected" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => setTarget({ row: r, status: "rejected" })}
                        >
                          Reject
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={Boolean(target)} onClose={() => !saving && setTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {target?.status === "approved" ? "Approve this content?" : "Reject this content?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {target?.status === "approved" ? (
              <>
                <strong>{target?.row?.title}</strong> will become visible to every student in{" "}
                <strong>{target?.row?.group?.name || "the group"}</strong>, and they will be notified.
              </>
            ) : (
              <>
                <strong>{target?.row?.title}</strong> will stay hidden from students. The tutor who
                uploaded it is told, along with any notes you leave.
              </>
            )}
          </DialogContentText>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            label={target?.status === "approved" ? "Notes (optional)" : "Reason (optional)"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            color={target?.status === "approved" ? "success" : "error"}
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving…" : target?.status === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
